/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/PageSelector/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { Box, IconButton, TextField, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "./PageSelector.module.css";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { useTranslation } from "react-i18next";

const PageSelector = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = {},
}) => {
  const { t } = useTranslation();
  const { deviceType, isSmallMobile, isMediumMobile } = useDeviceDetection();

  const isPrevDisabled = currentPage === 1 || disabled.prev;
  const isNextDisabled = currentPage >= totalPages || disabled.next;

  const getWidth = () => {
    switch (deviceType) {
      case "smallMobile":
        return 40;
      case "mediumMobile":
        return 45;
      default:
        return 50;
    }
  };

  const getPadding = () => {
    switch (deviceType) {
      case "smallMobile":
        return "3px 8px";
      case "mediumMobile":
        return "4px 10px";
      default:
        return "5px 10px";
    }
  };

  const getFontSize = () => {
    switch (deviceType) {
      case "smallMobile":
        return "14px";
      case "mediumMobile":
        return "15px";
      default:
        return "16px";
    }
  };

  const getNegativeMargin = () => {
    switch (deviceType) {
      case "smallMobile":
        return "-8px";
      case "mediumMobile":
        return "-10px";
      default:
        return "0px";
    }
  };

  const getMargin = () => {
    switch (deviceType) {
      case "smallMobile":
        return "12px";
      case "mediumMobile":
        return "14px";
      default:
        return "0px";
    }
  };

  return (
    <div className={styles.pageSelectorContainer}>
      <div className={styles.pageSelector}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ flexWrap: "nowrap", marginLeft: getMargin() }}
        >
          <IconButton
            onClick={() => onPageChange("prev")}
            disabled={isPrevDisabled}
            sx={{
              marginRight: getNegativeMargin(),
            }}
          >
            <ChevronLeftIcon sx={{ color: "#231F20" }} />
          </IconButton>

          {!isSmallMobile && !isMediumMobile && (
            <Typography variant="body1">
              {t("components.pageSelector.page")}
            </Typography>
          )}

          <TextField
            value={currentPage}
            size="small"
            sx={{
              width: getWidth(),
              textAlign: "center",
              backgroundColor: "#fefefe",
              borderRadius: "8px",
              "& input": {
                textAlign: "center",
                padding: getPadding(),
                fontSize: getFontSize(),
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#231F20",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#231F20",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#231F20",
              },
            }}
            readOnly
          />

          <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
            {t("components.pageSelector.of")}
          </Typography>
          <Typography variant="body1">{totalPages}</Typography>

          <IconButton
            onClick={() => onPageChange("next")}
            disabled={isNextDisabled}
            sx={{
              marginLeft: getNegativeMargin(),
            }}
          >
            <ChevronRightIcon sx={{ color: "#231F20" }} />
          </IconButton>
        </Box>
      </div>
    </div>
  );
};

export default PageSelector;
